/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import { default as fragments } from '../../fragments'
import { withRouter } from 'react-router-dom'
import moment from 'moment'

import {
  Table,
  SvgIcon,
  Spinner,
  PageNavigationButtons,
  SpanWithData,
  Warning
} from '../'

import {
  getAvatarInitialsColour,
  decodeBase64,
  getNodeTypeFromBase64Id,
  PageNavigationControl,
  getPageCursor
} from '../../utils'


class ObjectCases extends React.PureComponent {
  static propTypes = {
    object: PropTypes.string.isRequired,
    onCasesFetched: PropTypes.func,
    // TODO: specify which fetched fields to match to corresponding column names to be displayed instead of default
    // columns: PropTypes.arrayOf(PropTypes.shape({
    //   field: PropTypes.string.isRequired,
    //   columnName: PropTypes.string.isRequired
    // })),
    caseStatus: PropTypes.string,
    resultsPerPage: PropTypes.number,
    condensedView: PropTypes.bool,
    className: PropTypes.string,
    pageBasedPagination: PropTypes.bool,
    noRecordsToDisplayText: PropTypes.string
  }

  static defaultProps = {
    condensedView: false,
    pageBasedPagination: false,
    resultsPerPage: 15,
    noRecordsToDisplayText: 'No case records to display.'
  }

  state = {
    refetch: false,
    pageNav: new PageNavigationControl(),
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.error && !this.state.refetch) {
      this.setState({ refetch: true })
      nextProps.refetch()
    }

    if (this.state.refetch) {
      this.setState({ refetch: false })
    }

    if (nextProps.caserelations) {
      this.state.pageNav.setPageStates(this.props.caserelations, nextProps.caserelations)
    }
  }

  render() {
    if (this.props.loading) {
      return <Spinner type='brand' className='slds-is-relative' />
    }

    if (this.props.error) {
      return (
        <Warning className={this.props.className ? this.props.className : ''} iconSize='medium' iconMargin='medium'>
          Error loading case data.
        </Warning>
      )
    }

    if (this.props.caserelations.edges.length === 0) {
      return (
        <div className='slds-m-left_large'>
          <SvgIcon
            variant='utility'
            symbol='info'
            size='small'
            colour='light'
            className='slds-m-right_small'
          />
          {this.props.noRecordsToDisplayText}
        </div>
      )
    }

    let pageNav = this.state.pageNav
    let tableColumns = this.props.condensedView
      ? ["Product", "Request", "Priority", "Assigned to"]
      : ["Product", "Request", "Priority", "Created", "Created by", "Last/next action", "Assigned to", "Status"]

    let startCursor = this.props.caserelations.pageInfo.startCursor
    let totalCount = this.props.caserelations.totalCount

    return (
      <RefetchWrapper ref={this.props.forwardedRef} updateData={this.props.refetch}>
        <div className={`slds-scrollable_x ${this.props.className ? this.props.className : ''}`}>
          <Table
            columns={tableColumns}
            rows={this.props.caserelations.edges.map((caseRelation, index) => {
              let caseDetails = caseRelation.node.case
              return (
                <CaseDetailsRow
                  caseRecord={caseDetails}
                  hideNameColumn={true}
                  onClick={(identityId, caseId) => (
                    this.props.history.push(`/os/identity/${identityId}/cases/${caseId}`)
                  )}
                  condensedView={this.props.condensedView}
                  key={`caseDetailsRow_${index}`}
                />
              )
            })}
            objectRows={true}
          />
          {this.props.pageBasedPagination
            ? <PageNavigationButtons
                onNextClick={() => pageNav.nextPage(this.props.fetchNext)}
                onPreviousClick={() => pageNav.previousPage(this.props.fetchPrevious)}
                previousDisabled={pageNav.firstPage}
                nextDisabled={pageNav.lastPage}
                startCursor={startCursor}
                totalCount={totalCount}
                resultsPerPage={this.props.resultsPerPage}
                resultsText='cases'
                onPageNumberClick={pageNumber => this.props.refetch({ cursor: getPageCursor(pageNumber, this.props.resultsPerPage) })}
                className="slds-p-around_medium"
              />
            : <PageNavigationButtons
                onNextClick={() => pageNav.nextPage(this.props.fetchNext)}
                onPreviousClick={() => pageNav.previousPage(this.props.fetchPrevious)}
                previousDisabled={pageNav.firstPage}
                nextDisabled={pageNav.lastPage}
                className="slds-p-around_medium slds-float_right"
              />
          }
        </div>
      </RefetchWrapper>
    )
  }
}

class RefetchWrapper extends React.Component {
  render() {
    return this.props.children
  }
}

const CaseDetailsRow = ({ caseRecord, hideNameColumn, onClick, condensedView }) => {
  let caseDetails = caseRecord
  let priority = caseDetails.priority === 'A'
    ? 'high'
    : caseDetails.priority === 'B'
      ? 'medium'
      : 'low'
  let assignedToName = caseDetails.assignedTo.firstName + ' ' + caseDetails.assignedTo.lastName
  let assignedToInitials = `${caseDetails.assignedTo.firstName[0]}${caseDetails.assignedTo.lastName[0]}`
  let assignedToAvatarInitialsColour = getAvatarInitialsColour(assignedToInitials)

  if (condensedView) {
    return (
      <tr className="case-table-row" onClick={() => onClick(caseDetails.affects.id, caseDetails.id)}>
        {!hideNameColumn &&
          <th scope="row" data-label="Name">
            <div className="slds-truncate" title="Client name">
              {caseDetails.affects.firstName + ' ' + caseDetails.affects.lastName}
            </div>
          </th>
        }
        <td data-label="Product">
          <div className="slds-truncate" title="Product">
            <SpanWithData forField="CaseProductType" searchKey={caseDetails.productType} />
          </div>
        </td>
        <td data-label="Request">
          <div className="slds-truncate" title="Case type">
            {caseDetails.requestType}
          </div>
        </td>
        <td data-label="Priority">
          <div className="slds-truncate slds-m-left_small" title={priority}>
            <SvgIcon
              className="slds-m-right_x-small"
              variant="picsaos"
              symbol={`priority_${priority}`}
              size="small"
            />
          </div>
        </td>
        <td data-label="Assigned to">
          <div className="slds-truncate" title="Assigned to">
            <div className="case-assignee">
              <div className="slds-avatar slds-avatar_small slds-avatar_circle slds-m-right_x-small">
                <abbr
                  className={`slds-avatar__initials slds-icon-standard-user ${assignedToAvatarInitialsColour ? `avatar-color_${assignedToAvatarInitialsColour}` : ''}`}
                  title={assignedToName}>
                  {assignedToInitials}
                </abbr>
              </div>
              <div className="text-follows-icon case-assignee_name">
                {assignedToName}
              </div>
            </div>
          </div>
        </td>
      </tr>
    )
  } else {
    let createdByName = caseDetails.createdBy.identity.firstName + ' ' + caseDetails.createdBy.identity.lastName
    let createdByInitials = `${caseDetails.createdBy.identity.firstName[0]}${caseDetails.createdBy.identity.lastName[0]}`
    let createdByAvatarInitialsColour = getAvatarInitialsColour(createdByInitials)
    let nextActionAt = moment(caseDetails.nextActionAt)
    let today = moment()

    return (
      <tr className="case-table-row" onClick={() => onClick(caseDetails.affects.id, caseDetails.id)}>
        {!hideNameColumn &&
          <th scope="row" data-label="Name">
            <div className="slds-truncate" title="Client name">
              {caseDetails.affects.firstName + ' ' + caseDetails.affects.lastName}
            </div>
          </th>
        }
        <td data-label="Product">
          <div className="slds-truncate" title="Product">
            <SpanWithData forField="CaseProductType" searchKey={caseDetails.productType} />
          </div>
        </td>
        <td data-label="Request">
          <div className="slds-truncate" title="Case type">
            {caseDetails.requestType}
          </div>
        </td>
        <td data-label="Priority">
          <div className="slds-truncate slds-m-left_small" title={priority}>
            <SvgIcon
              className="slds-m-right_x-small"
              variant="picsaos"
              symbol={`priority_${priority}`}
              size="small"
            />
          </div>
        </td>
        <td data-label="Created">
          <div className="slds-truncate" title="Created">
            {moment(caseDetails.createdAt).format("DD MMM HH:mm")}
          </div>
        </td>
        <td data-label="Created by">
          <div className="slds-truncate" title="Created by">
            <div className="case-assignee">
              <div className="slds-avatar slds-avatar_small slds-avatar_circle slds-m-right_x-small">
                <abbr
                  className={`slds-avatar__initials slds-icon-standard-user ${createdByAvatarInitialsColour ? `avatar-color_${createdByAvatarInitialsColour}` : ''}`}
                  title={createdByName}>
                  {createdByInitials}
                </abbr>
              </div>
              <div className="text-follows-icon case-assignee_name">
                {createdByName}
              </div>
            </div>
          </div>
        </td>
        {caseDetails.status === 'RESOLVED'
          ? <td data-label="Last action">
              <div className="slds-truncate" title="Last action">
                {moment(caseDetails.updatedAt).format("DD MMM YYYY HH:mm")}
              </div>
            </td>
          : <td data-label="Next action">
              <div className={`slds-truncate${nextActionAt.isBefore(today) ? " slds-text-color_error": ''}`} title="Next action">
                {nextActionAt.format("DD MMM HH:mm")}
              </div>
            </td>
        }
        <td data-label="Assigned to">
          <div className="slds-truncate" title="Assigned to">
            <div className="case-assignee">
              <div className="slds-avatar slds-avatar_small slds-avatar_circle slds-m-right_x-small">
                <abbr
                  className={`slds-avatar__initials slds-icon-standard-user ${assignedToAvatarInitialsColour ? `avatar-color_${assignedToAvatarInitialsColour}` : ''}`}
                  title={assignedToName}>
                  {assignedToInitials}
                </abbr>
              </div>
              <div className="text-follows-icon case-assignee_name">
                {assignedToName}
              </div>
            </div>
          </div>
        </td>
        <td data-label="Status">
          <SpanWithData forField="CaseStatus" searchKey={caseDetails.status} />
        </td>
      </tr>
    )
  }
}

const getCaseRelations = gql`
  query($objectId: UUID, $contentTypeModel: String!, $caseStatus: String, $resultsPerPage: Int, $cursor: String) {
    caserelations(objectId: $objectId, contentType_Model: $contentTypeModel, case_Status: $caseStatus, orderBy: "-created_at", first: $resultsPerPage, after: $cursor) {
      totalCount
      edges {
        node {
  				id
          case {
            ...CaseInformation
          }
          contentType {
            id
            model
          }
          objectId
          createdAt
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${fragments.caseInformation}
`

const getPreviousCaseRelations = gql`
  query($objectId: UUID, $contentTypeModel: String!, $caseStatus: String, $resultsPerPage: Int, $cursor: String) {
    caserelations(objectId: $objectId, contentType_Model: $contentTypeModel, case_Status: $caseStatus, orderBy: "-created_at", last: $resultsPerPage, before: $cursor) {
      totalCount
      edges {
        node {
  				id
          case {
            ...CaseInformation
          }
          contentType {
            id
            model
          }
          objectId
          createdAt
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
  ${fragments.caseInformation}
`

const withData = graphql(getCaseRelations, {
    options: ({ object, caseStatus='', resultsPerPage = 15 }) => {
      let nodeType = getNodeTypeFromBase64Id(object)
      return {
        variables: {
          objectId: decodeBase64(object),
          contentTypeModel: nodeType.toLowerCase().replace('node', ''),
          caseStatus,
          resultsPerPage
        },
        fetchPolicy: 'network-only'
      }
    },
    props: ({ ownProps: { object, model, caseStatus, resultsPerPage = 15 }, data: { loading, error, refetch, caserelations, fetchMore } }) => {
      let objectId = decodeBase64(object)
      let modelName = getNodeTypeFromBase64Id(object).toLowerCase().replace('node', '')
      return {
        loading,
        error,
        refetch,
        caserelations,
        fetchNext: () => {
          return fetchMore({
            query: getCaseRelations,
            variables: {
              objectId,
              contentTypeModel: modelName,
              caseStatus,
              resultsPerPage,
              cursor: caserelations.pageInfo.endCursor
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
              const newEdges = fetchMoreResult.caserelations.edges
              const pageInfo = fetchMoreResult.caserelations.pageInfo
              return {
                caserelations: {
                  __typename: previousResult.caserelations.__typename,
                  edges: [...newEdges],
                  pageInfo
                }
              }
            }
          })
        },
        fetchPrevious: () => {
          return fetchMore({
            query: getPreviousCaseRelations,
            variables: {
              objectId,
              contentTypeModel: modelName,
              caseStatus,
              resultsPerPage,
              cursor: caserelations.pageInfo.startCursor
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
              const newEdges = fetchMoreResult.caserelations.edges
              const pageInfo = fetchMoreResult.caserelations.pageInfo
              return {
                caserelations: {
                  __typename: previousResult.caserelations.__typename,
                  edges: [...newEdges],
                  pageInfo
                }
              }
            }
          })
        }
      }
    }
  })

export default compose(
  withData,
  withRouter
)(ObjectCases)



// WEBPACK FOOTER //
// ./src/components/object-cases/index.js